import { useUploadTellescopeFile } from "@tellescope/tanstack-query";
import { Editor } from "@tiptap/react";
import { useState } from "react";

const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif"];
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

/**
 * State for {@link ImageButton}: the popover, the manual-URL draft, and the upload flow. Both paths
 * end the same way — {@link insertImage} places the `src` into the TipTap document and closes the
 * popover. Uploads go through {@link useUploadTellescopeFile} with `returnPublicURL` so the image
 * renders in email/unauthenticated contexts, mirroring the booking-page image field.
 */
export const useImageButton = (editor: Editor | null) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const { uploadFileAsync, progress } = useUploadTellescopeFile({
    returnPublicURL: true,
    allowedFileTypes: ALLOWED_IMAGE_TYPES,
    maxFileSize: MAX_IMAGE_SIZE_BYTES,
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setUrl("");
    setOpen(nextOpen);
  };

  const insertImage = (src: string) => {
    if (!editor || !src) return;
    editor.chain().focus().setImage({ src }).run();
    setOpen(false);
  };

  const applyUrl = () => insertImage(url.trim());

  const uploadImage = async (file: File): Promise<string> => {
    const { publicURL } = await uploadFileAsync({ name: file.name, size: file.size, type: file.type }, file);
    if (!publicURL) throw new Error("Upload did not return a public URL");
    return publicURL;
  };

  return { open, url, setUrl, progress, handleOpenChange, applyUrl, insertImage, uploadImage };
};
